import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "./ui/button";
import { createContext, Suspense, use, useContext, useEffect, useRef, useState } from "react";
import { Badge } from "./ui/badge";

import { Star, Clock, Tv, Clapperboard, Music2, Disc3, CircleUserRound, List, CalendarClock, Heart, Mic2, PencilLine, Ghost, Calendar, EyeOff } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card"
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import { useLocation, useNavigate } from 'react-router-dom'

import { FetchProfile, FetchUserLists, GetConsumed, GetNotConsumed } from "./ApiRoutes.tsx";
import { AnimatedBorder } from "./AnimatedBorder.tsx";
import { matchListTag, mediaTypeIcon, truncateText } from "@/utils/TextUtils.tsx";
import MainLoader, { SecondaryLoader } from "@/utils/Loaders.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,   
  DialogHeader,     
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { CustomizeProfileForm, EditProfileForm } from "./FormComponents.tsx";
import { Input } from "./ui/input.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select.tsx";
import {
  useQuery,
} from '@tanstack/react-query'
import { TimeLike } from "fs";
import { ItemDetails } from "./ListViewer.tsx";
import { UserContext } from "@/App.tsx";

export interface ProfileFeaturedType {
    id:number;
    order:number;
    item_type:"list"|"movies"|"shows"|"albums"|"songs";
    internal_list_details:null|{
        id:number;
        owner:string;
        title:string;
        description:string;
        list_type:"movies"|"shows"|"albums"|"songs"|"artists";
        tag:string
        is_public:boolean;
        is_ranked:boolean;
        created_at:TimeLike;
        updated_at:TimeLike;
        items:[
            {
                id:number;
                order:number;
                comment:string;
                item_id:number;
                external_id:string;
                title:string;
                year:number;
                image_url:string;
                artist:string|null;
            }
        ]
    }
    external_id:string|null;
    title:string;
    image:string|null; // exists if not list
    artist:string|null; // depends on the item
    year:string|null;
    comment:string|null;
}


interface ProfileDataType {
        user:string;
        username:string;
        email:string;
        bio:string;
        avatar:string;
        list_count:number;
        background:string;
        points:number;
        featured_items:ProfileFeaturedType[];
}

export const ProfileContext = createContext<ProfileDataType | null>(null);

export default function Profile () {
    const location = useLocation();
    const navigate = useNavigate();
    const {user, setUser} = useContext(UserContext);
    const [isOwner, setIsOwner] = useState(false);
    const [profileError, setProfileError] = useState(false);
    const [profileExists,setProfileExists] = useState(true);
    const [isListsLoaded, setIsListsLoaded] = useState(false);
    // home | lists | stats
    const [activeTab, setActiveTab] = useState<"home"|"lists"|"stats">("home");

    const [profileUsername, setProfileUsername] = useState(""); 
    const username = user.username || localStorage.getItem("username");


    const {isPending: isPending, isError, data: profileData, isSuccess, error } = useQuery({
        queryKey: ['profile', profileUsername],
        queryFn: () => FetchProfile(profileUsername),
        enabled: !!profileUsername,
    });

    const { isPending: isListsPending, isError: isListsError, data: listData, error: listsError, isSuccess: isListsSuccess } = useQuery({
        queryKey: ['profileList', profileUsername],
        queryFn: () => FetchUserLists(profileUsername),
        enabled: !!profileUsername, // Only fetch if we have a username
    });


    // load profile data once username is fetched from url
    useEffect(()=> {
        setProfileError(false);
        const pathSegments = location.pathname.split('/');
        const profileSegmentIndex = pathSegments.indexOf("profile");
        let currentProfilePageUsername = "";

        if (profileSegmentIndex !== -1 && profileSegmentIndex < pathSegments.length - 1) {
            currentProfilePageUsername = pathSegments[profileSegmentIndex + 1];
        }

        setProfileUsername(currentProfilePageUsername);

        if (currentProfilePageUsername && username) {
            setIsOwner(currentProfilePageUsername === username);
            
        } else {
            setIsOwner(false); 
        }
    document.title = currentProfilePageUsername ? `@${currentProfilePageUsername} — MediaHarbor` : "Profile — MediaHarbor";
        
    },[location.pathname,username])

   
    useEffect(()=>{
        if(isOwner && profileData?.username){
            localStorage.setItem("avatar", profileData.avatar);
        }
    }, [profileData, username, isOwner]);

   if(isPending){
    return(
        <MainLoader />
    )
   }
    if (!profileExists){
    return(
        <div className="flex flex-col justify-center items-center text-neutral-500 mt-15 font-semibold">
                <h3 className="text-2xl"> Profile '{profileUsername}' does not exist :/</h3>
                <p className="text-neutral-600 font-medium">Did you spell it correctly?</p>
        </div>
    )
   }
   else if (error){
    return(
        <div className="flex flex-col gap-2 justify-center items-center text-center text-neutral-500 mt-15 font-semibold">
                <h3 className="text-xl sm:text-2xl"> Something went wrong loading {profileUsername}'s profile :/</h3>
                <p className="text-neutral-600 font-medium">We may be expierencing issues, try again later</p>
                <Ghost className="mt-5 w-10 h-10 text-neutral-700" />
        </div>
    )
   }
   else if(isSuccess && profileData){
    return(
        <ProfileContext.Provider value={profileData}>
        <>
        <section className="flex justify-center relative w-full h-65 overflow-hidden">
            
            <div
                className="absolute inset-0 bg-center filter blur-xs scale-140 skew-3"
                style={profileData.background ? {  backgroundImage: `url(${profileData.background})` } : {backgroundImage: "radial-gradient(circle,rgba(31, 38, 61, 1) 0%, rgba(10, 10, 28, 1) 48%, rgba(0, 0, 0, 1) 100%)"}}
            />
            
            <div className="flex absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 z-10" />
            <div className="absolute bottom-0 left-0 w-full rotate-180 overflow-hidden">
                    <svg className="relative block w-full" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                        <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" class="shape-fill"></path>
                        <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" class="shape-fill"></path>
                        <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" class="shape-fill"></path>
                    </svg>
                </div>
            <header className="relative z-10 flex justify-between items-end sm:px-5 md:px-10 lg:px-20 pb-5 h-65 w-full max-w-300 lg:justify-center">
                
                
                <section className="relative flex items-center gap-4 w-full pl-6 md:pl-0">
                    { isOwner && 
                        <EditProfileDialogue username={username} bio={profileData.bio ? profileData.bio : ""} />
                    }
                    
                    
                    <Avatar className="w-22 h-22 border-2 border-white">
                        <AvatarImage src={profileData.avatar ? profileData.avatar : undefined} />
                        <AvatarFallback>:)</AvatarFallback>
                    </Avatar>

                    <div className="flex flex-col gap-0.5 text-white">
                        <h2 className="font-bold text-2xl text-shadow-lg">@{profileUsername}</h2>
                        <p className="text-sm max-w-40 break-words md:max-w-60 md:pr-5 text-neutral-300">{profileData.bio ? profileData.bio : ""}</p>
                    </div>
                </section>
                <section className="z-20 flex flex-col pr-6 md:pr-0 md:flex-row gap-4 md:gap-6"> 
                        
                    <div className="flex flex-col items-center hover:cursor-pointer" onClick={() => setActiveTab("lists")}>
                        <h4 className="text-white font-bold text-3xl text-shadow-lg">
                            {profileData.list_count}
                        </h4>
                        <p className="text-white text-sm text-shadow-lg">
                            Lists
                        </p>
                    </div>

                    <div className="hidden flex-col items-center">
                        <h4 className="text-white font-bold text-3xl text-shadow-lg">
                            {profileData.points}
                        </h4>
                        <p className="text-white text-sm text-shadow-lg">
                            Points
                        </p>
                    </div>
                        
                    
                    
                </section>
            </header>
            

        </section>
        <div className="flex top-0 sticky justify-center bg-black z-50">
            <div className={`flex items-center w-full h-10 bg-black max-w-300 `}>
                <Button onClick={() => setActiveTab("lists") } className={`hover:cursor-pointer rounded-none bg-black h-full text-lg w-1/4 
                    ${activeTab == "lists" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                        Lists
                </Button>

                <Button onClick={() => setActiveTab("home")} className={`hover:cursor-pointer rounded-none bg-black h-full text-lg w-2/4 
                    ${activeTab == "home" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                        Home
                </Button>

                <Button onClick={() => setActiveTab("stats")} className={`hover:cursor-pointer rounded-none bg-black h-full text-lg w-1/4 
                    ${activeTab == "stats" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                        Stats
                </Button>
            </div>
        </div>
        
        {/* active tab determines what content is shown in profile body */}
        <main className="px-5 flex flex-col items-center min-h-screen w-full bg-black pb-5">
            {
                activeTab == "home" &&
                <ProfileHome />
            }
            {
                activeTab == "lists" &&
                <ProfileLists profileUsername={profileUsername} isListsLoaded={isListsLoaded} 
                setIsListsLoaded={setIsListsLoaded} listData={listData} isPending={isListsPending} isError={isListsError} error={listsError} isSuccess={isListsSuccess}/>
            }

            {
                activeTab == "stats" &&
                <div className="flex flex-col justify-center items-center text-white mt-15 font-semibold">
                    <h3 className="text-2xl"> Coming soon!</h3>
                    <p className="text-neutral-600 font-medium">It'll be cool I promise</p>
                </div>
            }


            
            
        </main>
        </>
        </ProfileContext.Provider>
    )
   }
   
   // https://claude.ai/public/artifacts/3844eea0-7eaa-4131-9b78-1bfa88d65751
    
}


const itemsAlbums = [
    {image_url:"https://i.scdn.co/image/ab67616d0000b2737005885df706891a3c182a57", title:"the incredible true story", artist:"tyler - the creator", year:"2018"}, 
    {image_url:"https://i.scdn.co/image/ab67616d0000b27322007a4aa8a55fe7b1de5632", title:"wolf", artist:"tyler - the creator", year:"2018"},
    {image_url:"https://i.scdn.co/image/ab67616d0000b27322007a4aa8a55fe7b1de5632", title:"wolf", artist:"tyler - the creator", year:"2018"},
    {image_url:"https://i.scdn.co/image/ab67616d0000b27322007a4aa8a55fe7b1de5632", title:"wolf", artist:"tyler - the creator", year:"2018"},
    ]



function ProfileHome() {
    
    
    return(
        <section className="relative max-w-150 w-full flex flex-col gap-7 pt-5 justify-center items-center overflow-hidden sm:overflow-visible">
            <Suspense fallback="loading">
                <RecentlyConsumed />
            </Suspense>
            
            
                
            <div className="relative flex">
                <FeaturedCarousel />
                <div className="flex justify-center items-center">
                    <Heart className="absolute z-0 w-150 h-150 -right-30 sm:w-full sm:bottom-10 sm:right-55 sm:-rotate-10 sm:h-100 text-white/10" />
                </div>
            </div> 
           
           
            <ConsumeLater />
        
        </section>
    )
}

function FeaturedCarousel() {
    const [api, setApi] = useState<CarouselApi>()
    const [current, setCurrent] = useState(0)
    const [count, setCount] = useState(0)
    // we wont specify a null condition as this component 
    // cant be rendered unless profile data exists
    const context = useContext(ProfileContext);

    useEffect(() => {
        if (!api) {
          return
        }
     
        setCount(api.scrollSnapList().length)
        setCurrent(api.selectedScrollSnap() + 1)
     
        api.on("select", () => {
          setCurrent(api.selectedScrollSnap() + 1)
        })
      }, [api])

    if(!context || context.featured_items.length < 1){return;}

    return(
        
    <div className="flex flex-col w-full z-20 bg-transparent">
        
        <Carousel className="w-90 sm:w-150 h-fit overflow-hidden md:overflow-visible 
        hover:cursor-grab active:cursor-grabbing" 
        setApi={setApi}
        orientation="horizontal"
        opts={{ 
            align: "center", 
            loop: true,     
        }}>
            
            <CarouselContent>
            {
            context?.featured_items.length > 0 && context.featured_items.map((item,index)=> (
                <CarouselItem key={index} className="flex w-fit justify-center items-center border-none">
                  <Card className="w-fit py-0 bg-transparent border-transparent">
                    <CardContent className="w-80 h-fit sm:w-150 max-w-full flex justify-center items-center">
                      <FeaturedItem id={item.internal_list_details?.id ?? item.external_id} title={item.title} type={item.item_type == "list" ? "list" : "item"}  
                      itemType={item.item_type != "list" ? item.item_type : ""} listType={item.internal_list_details?.list_type} note={item.internal_list_details?.description ?? item.comment} 
                      lastUpdated={item.internal_list_details?.updated_at} year={item.year} artist={item.artist ?? ""} tag={item.internal_list_details?.tag} items={item.internal_list_details?.items} image={item.image ?? ""}
                      />
                    </CardContent>
                  </Card>
                </CarouselItem>
            ))
            
            }
        </CarouselContent>
      <CarouselPrevious />
      <CarouselNext />
    </Carousel>
        <div className="py-2 text-center text-sm text-muted-foreground font-semibold">
            {current}/{count}
        </div>
    </div>
    )
}

interface FeaturedItemProps {
    id:number,
    title:string;
    type:"list"|"item";
    itemType?:string;
    listType?:string;
    note:string;
    items?:{
        title:string;
        image_url:string;
        note:string;
    }[];
    lastUpdated:string;
    link?:string;
    artist?:string;
    rating?:string;
    year?:string;
    when?:string; // when did the user first expierence an item
    tag?:string;
    image?:string;
    
}

// edit to show top 3 items in each list: https://claude.ai/public/artifacts/19d88916-9aab-4426-bdfe-febe0ee8664a
// https://claude.ai/public/artifacts/a062fd9b-bde4-42b4-b0ab-17f977898d5f
function FeaturedItem ({id, title="Hot Fuzz", 
    note="this list makes me think about lots of things. mainly things about despair and darkness among other much cooler things", type="item", 
    listType, itemType="movies", image, items, link="/", artist="Simon Pegg", rating="8", year="2018", when="187d", tag}:FeaturedItemProps) {    
    const shownItems = 3;
    let navigate = useNavigate();
    
    return(
        <article className="flex flex-col justify-center h-130">
            {
                type == "list" &&
                <>
                <section className="flex flex-col bg-neutral-950 h-full w-full rounded-lg min-w-80" 
                >
                    
                    <div className="flex h-2/5 w-full bg-center backdrop-blur-2xl rounded-t-2xl" aria-label="list image preview" style={{ backgroundImage: `url(${items[0].image_url})` }}>
                        <div className="flex absolute inset-0 bg-gradient-to-t from-black/80 to-black/20 z-10">
                            <List className="absolute top-4 right-4 text-white" />
                            <div className="absolute bottom-2 left-4 flex flex-col">
                                <h4 className="text-lg font-semibold text-white">
                                    {title}
                                </h4>
                                <p className="text-sm text-neutral-400">
                                    {note}
                                </p> 
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col h-3/5 justify-evenly px-3 pt-3">
                        <div className="flex justify-between -mt-5 ">
                            <div className="flex gap-2 flex-wrap wrap-anywhere max-w-3/4">
                                

                                {
                                 listType && <Badge className="font-semibold capitalize">{listType}</Badge>
                                }

                                {
                                 tag && <Badge className="font-semibold bg-purple-800 capitalize">{tag}</Badge>
                                }
                                
                                
                            </div>
                        </div>
                        
                        
                        <div className=" flex flex-col gap-2 items-center justify-center flex-wrap ">
                        { listType == "songs" || listType == "albums" || listType == "artists" ?
                            items.slice(0,shownItems).map((item,index) => 
                                
                                <AnimatedBorder index={index}>
                                    <ItemDetails id={item.external_id} itemType={listType}>
                                        <article className={`flex justify-start items-center gap-3 w-full px-2 py-1 ${index > 0 ? "bg-black/30": "bg-black/50"} rounded-md  hover:cursor-pointer`} key={index}>
                                            <aside className="flex gap-2 justify-center items-center"> 
                                                <h5 className=" text-white/50 text-xl flex justify-center items-center font-bold h-5 w-5">{index+1}</h5> 
                                                <img draggable="false" src={item.image_url} className="h-12 rounded-md" />
                                            </aside>
                                            <div className="flex text-white items-center w-40">
                                                <h6 className="font-semibold text-xs">{truncateText(item.title,50)}</h6>
                                            </div>
                                        </article>
                                    </ItemDetails>
                                </AnimatedBorder>
                                
                            )
                            : // currently this would mean if movies or shows, may need refactor if other media types exist
                            items.slice(0,shownItems).map((item,index) => 
                                <AnimatedBorder index={index}>
                                    <ItemDetails id={item.external_id} itemType={listType}>
                                        <article className={`flex justify-start items-center gap-3 w-full px-2 py-1 ${index > 0 ? "bg-black/30": "bg-black/50"} rounded-md hover:cursor-pointer`} key={index}>
                                            <aside className="flex gap-2 justify-center items-center"> 
                                                <h5 className=" text-white/50 text-xl flex justify-center items-center font-bold h-5 w-5">{index+1}</h5> 
                                                <img draggable="false" src={item.image_url} className="h-12 rounded-md" />
                                            </aside>
                                            <div className="flex text-white items-center w-50 ">
                                                <h6 className="font-semibold text-xs">{truncateText(item.title,50)}</h6>
                                            </div>
                                        </article>
                                    </ItemDetails>
                                </AnimatedBorder>
                                
                            )
                        }

                            <Button onClick={() => navigate(`/list/view/${id}`)} className="w-full text-neutral-200 hover:cursor-pointer">
                                <p className="text-neutral-500 text-sm">{items.length > shownItems ? " View " + (items.length - shownItems) + " others": "View list"} </p>
                            </Button>
                        </div>
                     
                    </div>
                    
                    
                </section>

                
                </>
            }

            {
                type == "item" &&
                <>
                <ItemDetails id={id} itemType={itemType} >
                <section className="flex flex-col bg-neutral-950 h-110 max-w-100 rounded-lg min-w-80 hover:cursor-pointer" >
                    
                    <div className="flex h-7/8 w-full bg-center bg-cover backdrop-blur-2xl rounded-t-2xl" aria-label="list image preview" style={{ backgroundImage: `url(${image})` }}>

                        <div className="flex absolute inset-0 bg-gradient-to-bl from-black/20 to-neutral-950/0 z-10">
                        
                        { itemType == "shows" &&
                            <Tv className="absolute top-4 right-4 text-white" />
                        }
                        { itemType == "movies" &&
                            <Clapperboard className="absolute top-4 right-4 text-white" />
                        }
                        { itemType == "songs" &&
                            <Music2 className="absolute top-4 right-4 text-white" />
                        }
                        { itemType == "albums" &&
                            <Disc3 className="absolute top-4 right-4 text-white" />
                        }
                        { itemType == "artists" &&
                            <CircleUserRound className="absolute top-4 right-4 text-white" />
                        }
                        
                        
                            
                        </div>
                    </div>
                    <div className="flex flex-col justify-between h-min-1/8 p-3 gap-4 items">
                        <div className="flex items-center justify-between h-fit text-white border-neutral-700 border-t-2 flex-wrap items pt-2">
                            {
                                artist &&
                                <div className="flex items-center justify-center">
                                    <p className="text-xs">{artist}</p>
                                    
                                </div>
                            }
                            {
                                year &&
                                <div className="flex items-center justify-between w-full">
                                    <h4 className="flex text-sm break-after-avoid max-w-[75%]  font-semibold text-white">
                                        {title}
                                    </h4>
                                    <p className="text-sm flex ml-4 gap-1 items-center"><span><Calendar className="h-4 w-4" /></span>  {year}</p>
                                </div>
                            }
                            
                        </div>
                        
                    </div>
                    
                </section>
                </ItemDetails>

                
            </>
            }
            
        </article>
    )
}

interface RecentlyConsumedProps {
    items:{
        title:string;
        image:string;
        artist?:string;
    }[]
}

const RecentlyConsumedItems = [{
    title:"Scott Pilgrim",
    image:"https://image.tmdb.org/t/p/w500//g5IoYeudx9XBEfwNL0fHvSckLBz.jpg",

},
{
    title:"One Punch Man",
    image:"https://image.tmdb.org/t/p/w500//jbYJuxfZMpYDalkiOnBcCv9TaL.jpg"
},
{
    title:"Romcom",
    image:"https://i.scdn.co/image/ab67616d0000b273a12c5353b3d0385c6ce8c103",
    artist:"Jakey",
},
{
    title:"Havoc",
    image:"https://image.tmdb.org/t/p/w500//r46leE6PSzLR3pnVzaxx5Q30yUF.jpg",
},
{
    title:"Mama's Boy",
    image:"https://i.scdn.co/image/ab67616d0000b273c5f98cb52cf88bbdcf2ae894",
    artist:"Dominic Fike",
},

]


function RecentlyConsumed ({items=RecentlyConsumedItems}:RecentlyConsumedProps){
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

    const profileData = useContext(ProfileContext);
    const navigate = useNavigate();

    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ['consumed', profileData?.username, "profile"],
        queryFn: () => GetConsumed(String(profileData?.username)),
        enabled: !!profileData?.username,
    });

    useEffect(() => {
        const handleResize = () => {
          setIsMobile(window.innerWidth <= 640);
        };
    
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
      }, []);
      
    let sortedResults = data?.results.sort((a:object, b:object) => new Date(b.consumed_at) - new Date(a.consumed_at))

    const visibleItems = isMobile ? sortedResults?.slice(0, 3) : sortedResults?.slice(0, 4);
    if(isPending){
        return(
            <main className="flex flex-col items-center justify-center min-w-80 w-full">
                <header className="flex gap-2 py-2 border-b-2 w-full rounded-t-2xl px-2 border-neutral-800 items-center">
                    <Clock className=" text-neutral-100 " /> <h3 className="text-white font-semibold">Recently Consumed</h3> 
                </header>

                <SecondaryLoader />
            </main>
        )
    }

    else if(isError){
        return(
            <main className="flex flex-col items-center justify-center min-w-80 w-full">
                <header className="flex gap-2 py-2 border-b-2 w-full rounded-t-2xl px-2 border-neutral-800 items-center">
                    <Clock className=" text-neutral-100 " /> <h3 className="text-white font-semibold">Recently Consumed</h3> 
                </header>

                <div className="flex flex-col items-center justify-center">
                    <h3 className="text-lg text-white">Something went wrong loading {profileData?.username}'s consumed</h3>
                    <p className="text-neutral-600 text-sm">We may be expierencing issues</p>
                </div>
                
            </main>
        )
    }

    else if(isSuccess && data && data.results.length < 1){
        return(

            <main className="flex flex-col items-center justify-center min-w-80 w-full gap-2">
                <header className="flex gap-2 py-2 border-b-2 w-full rounded-t-2xl px-2 border-neutral-800 items-center">
                    <Clock className=" text-neutral-100 " /> <h3 className="text-white font-semibold">Recently Consumed</h3> 
                </header>
                <div className="flex flex-col items-center justify-center">
                    <h3 className="text-lg text-white">{profileData?.username} hasn't consumed anything!</h3>
                    <p className="text-neutral-600 text-sm">maybe they're not hungry</p>
                </div>
               
            </main>
          
        )
    }

    else if(isSuccess && data){
        return(
            <article className="relative flex flex-col px-4 min-h-fit max-h-65 w-full bg-neutral-none rounded-lg min-w-90">
                <header className="flex justify-between gap-2 py-2 border-b-2  rounded-t-2xl px-2 border-neutral-800 items-center">
                    <div className="flex gap-1">
                        <Clock className=" text-neutral-100 " /> <h3 className="text-white font-semibold">Recently Consumed</h3> 

                    </div>
                   <Button onClick={() => navigate(`/consumelater/${profileData?.username}`)} className="h-7 hover:cursor-pointer">View</Button>
                </header>
                
                <section className="flex gap-3 py-4 overflow-hidden justify-center items-center duration-200 ease-in-out">
                
                    {visibleItems.map((item, index) => (
                    <ItemDetails id={item.item.external_id} itemType={item.item.item_type}>
                    <article key={index} className="flex flex-col items-center hover:cursor-pointer">
                        <img src={item.item.image_url} className={` ${item.item.item_type == "shows" || item.item.item_type == "movies" ? "h-30 sm:h-40"  : "h-25 sm:h-32"} w-auto rounded-lg ease-in-out duration-200 hover:scale-110 hover:rotate-5`} />
                    </article>
                    </ItemDetails>
                    ))}
        
                </section>
            </article>
        )
    }

    
    
}

interface ConsumeLaterProps {
    items: {
        title:string;
        image:string;
    }[];
}

function ConsumeLater (){
    let navigate = useNavigate();

    const profileData = useContext(ProfileContext);
    

    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ['consumed', profileData?.username, "profile", "later"],
        queryFn: () => GetNotConsumed(String(profileData?.username)),
        enabled: !!profileData?.username,
    });

    const itemStyle = status
        ? "line-through bg-neutral-200/20 from-neutral-700/60 to-neutral-600/30"
        : "bg-black/60 from-black/70 to-transparent";

    if(isPending){
        return(
            <ScrollArea onClick={() => navigate(`/consumelater/${profileData?.username}`)} className="flex flex-col min-w-80 w-full h-110 bg-neutral-950 rounded-lg px-4 hover:cursor-pointer">
                <header className="flex text-white gap-2 border-b-2 border-neutral-800 py-2 items-center">
                    <CalendarClock className="h-5 w-5" /> <h3 className="font-semibold">Consume Later</h3>
                </header>
                <section className="flex w-full h-full items-center justify-center">
                <SecondaryLoader />
                </section>
        </ScrollArea>
        )
         
    }
    if(isError){
        return(
            <ScrollArea onClick={() => navigate(`/consumelater/${profileData?.username}`)} className="flex flex-col min-w-80 w-full h-110 bg-neutral-950 rounded-lg px-4 hover:cursor-pointer">
                <header className="flex text-white gap-2 border-b-2 border-neutral-800 py-2 items-center">
                    <CalendarClock className="h-5 w-5" /> <h3 className="font-semibold">Consume Later</h3>
                </header>
                <section className="flex w-full h-full items-center justify-center">
                    <div className="flex flex-col items-center justify-center mt-10">
                        <h3 className="text-lg text-white">Something went wrong loading {profileData?.username}'s Consume Later</h3>
                        <p className="text-neutral-600 text-sm">We may be expierencing issues</p>
                    </div>
                </section>
            </ScrollArea>
        )
         
    }
    
    if(isSuccess && data && data.results.length < 1){
        return(
            <ScrollArea className="flex flex-col min-w-80 w-full h-110 bg-neutral-950 rounded-lg px-4 ">
                <header onClick={() => navigate(`/consumelater/${profileData?.username}`)} className="flex text-white gap-2 hover:cursor-pointer border-b-2 border-neutral-800 py-2 items-center">
                    <CalendarClock className="h-5 w-5" /> <h3 className="font-semibold">Consume Later</h3>
                </header>
                <section className="flex  w-full h-full items-center justify-center">
                    <div className="flex flex-col items-center justify-center mt-10">
                        <h3 className="text-lg text-white">{profileData?.username}'s Consume Later is empty!</h3>
                        <p className="text-neutral-600 text-sm">maybe they're all caught up?</p>
                        <Ghost className="text-neutral-500 mt-2" />
                    </div>
                </section>
            </ScrollArea>
        )
    }

    if(isSuccess && data){
        return(
            <section className="flex flex-col min-w-80 w-full h-110 rounded-lg px-4 gap-2 ">
                <header className="mb-2 flex justify-between text-white gap-2 border-b-2 border-neutral-800 p-2 items-center">
                    <div className="flex gap-1 items-center"><CalendarClock className="h-5 w-5" /> <h3 className="font-semibold">Consume Later</h3></div>
                    <Button onClick={() => navigate(`/consumelater/${profileData?.username}`)} className="h-7 hover:cursor-pointer">View</Button>
                </header>
                    {data.results.slice(0,5).map((item,index) =>
                    <article className="max-w-full px-2">
                        <ItemDetails id={item.item.external_id} itemType={item.item.item_type}>
                            <main className="text-white relative justify-center items-center flex hover:cursor-pointer h-15 rounded-2xl bg-center ease-in-out duration-200"
                                style={{ backgroundImage: `url(${item.item.image_url})` }}>
                                <div className={`group absolute flex justify-center items-center h-full w-full rounded-xl inset-0 bg-gradient-to-t duration-175 ease-in-out hover:bg-black/40 z-0 hover:backdrop-blur-none backdrop-blur-xs ${itemStyle}`}>
                                    <h3 className="group flex justify-center text-center font-semibold z-10 max-w-40 md:max-w-[80%]">{item.item.title}</h3>
                                    <div className="flex justify-center items-center absolute left-2.5 gap-2">{mediaTypeIcon(item.item.item_type)}</div>
                                </div>
                            </main>
                        </ItemDetails>
                    </article> 
                    )}
            </section>
        )
    }
}

interface ProfileListsProps {
    profileUsername:string;

    isListsLoaded: boolean;
    setIsListsLoaded: React.Dispatch<React.SetStateAction<boolean>>;

    listData: any;
    isPending: boolean;
    isError: boolean;
    error: Error | null;
    isSuccess:boolean;
}

interface ListProps {
    id:string,
    title:string;
    description:string;
    list_type:string;
    tag:string;
    updated_at:Date;
    is_ranked:boolean;
    is_public:boolean;
    profileUsername:string;
    owner:string;
    items:[
        {
            order:number;
            comment:string;
            external_id:string;
            title:string;
            year:string;
            image_url:string;
            artist?:string;
        }
    ]  
}

function ProfileLists ({profileUsername, listData, isPending, isError, error,isSuccess}:ProfileListsProps) {
    const [isLoading, setIsLoading] = useState(false);

    // for filtering lists
    const [listTitle, setListTitle] = useState("");
    const [mediaType, setMediaType] = useState("all");
    const [filteredListData, setFilteredListData] = useState([{}]);


    useEffect(() => {
        if(!listData) {
            setFilteredListData([]); // Set to empty array to avoid errors
            return;
        }

        if((mediaType == "all" || mediaType == "") && listTitle.trim() == ""){
            setFilteredListData(listData);
            return;
        }
        else if(mediaType && mediaType != "all" && listTitle.trim() != ""){
            setFilteredListData(listData.filter((value) => value.list_type == mediaType))
            setFilteredListData(filteredListData.filter((value) => value.title.toLowerCase().includes(listTitle.toLowerCase())))
        }
        else if(mediaType && mediaType != "all"){
            setFilteredListData(listData.filter((value) => value.list_type == mediaType))
        }
        else if(listTitle.trim() != ""){
            setFilteredListData(listData.filter((value) => value.title.toLowerCase().includes(listTitle.toLowerCase())))
        }
        else{
            setFilteredListData(listData);
        }


    },[mediaType, listTitle])


    if(error){
        return(
            <div className="text-white">Something went wrong</div>
        )
    }
    else if (isPending){
        return(
            <div className="flex text-neutral-500 italic flex-col items-center justify-center pt-5">
                <SecondaryLoader />
                <p className="mt-8">Fetching {profileUsername}'s lists</p>
            </div>
            
        )
    }
    else if(listData.length < 1){
        return(
            <article className="flex flex-col justify-center items-center text-neutral-500 mt-15 font-semibold">
                <h3 className="text-2xl text-white">{profileUsername} has no lists!</h3>
                <p className="text-neutral-600 font-medium">maybe they hate sequential items?</p>
            </article>
        )
    }
    if(isSuccess && listData){
        return(
        <section className="flex flex-col mt-5 min-w-80 sm:w-150 gap-6 overflow-x-hidden">
            <form className="flex w-full justify-center items-center gap-2 text-white">
                <Input className="w-30" placeholder="List title..."  onInput={e => setListTitle(e.currentTarget.value.trim())}></Input>  
                <Select value={mediaType} defaultValue={mediaType} onValueChange={value => setMediaType(value)}>
                            <SelectTrigger id="list-type" className=" sm:w-40 ">
                                <SelectValue placeholder="Media type" />
                            </SelectTrigger>
                            <SelectContent className="bg-black text-secondary">
                                <SelectItem className="hover:cursor-pointer" value="all">All types</SelectItem>
                                <SelectItem className="hover:cursor-pointer" value="albums">Albums</SelectItem>
                                <SelectItem className="hover:cursor-pointer" value="artists">Artists</SelectItem>
                                <SelectItem className="hover:cursor-pointer" value="songs">Songs</SelectItem>
                                <SelectItem className="hover:cursor-pointer" value="shows">Shows</SelectItem>
                                <SelectItem className="hover:cursor-pointer" value="movies">Movies</SelectItem>
                            </SelectContent>
                        </Select>
                <Button className="dark hover:cursor-pointer text-white" type="button" variant="outline"
                onClick={() => {setMediaType("all"); setListTitle("");}}>
                    Clear
                </Button>
            </form>
            
            <>
            { filteredListData.length >= 1 &&
                filteredListData.map((item,index)=> (
                   
                    <ListItem key={index} title={item.title} description={item.description} 
                    list_type={item.list_type} items={item.items} tag={item.tag} profileUsername={profileUsername} id={item.id}
                    is_public={item.is_public} owner={item.owner}
                    />
                    
                    
                ))   
            }
            { filteredListData.length < 1 &&
                <div className="flex flex-col justify-center items-center text-neutral-500 mt-15 font-semibold">
                    <h3 className="text-2xl">No lists found</h3>
                    <p className="text-neutral-600 font-medium">Try changing the filters?</p>
                </div>  
            }
            </>
            
        </section>
         
    )
    } 
}

const listTypeIcon = (list_type:string) => {
    let icon = null;

    switch (list_type) {
        case "albums":
            icon = <Disc3 />;
            break;

        case "shows":
            icon = <Tv />;
            break;
    
        case "movies":
            icon = <Clapperboard />;
            break;
        
        case "songs":
            icon = <Music2 />;
            break;
        case "artists":
            icon = <Mic2 />;
            break;
        default:
            break;
    }

    return icon;
}

function ListItem ({title, description, list_type, tag, items=[{}], profileUsername, id, is_public, owner}:ListProps){
    let navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
    const {user, setUser} = useContext(UserContext);
    const username:string = user?.username || localStorage.getItem("username") || "";
    const showHidden:boolean = (username === owner ?? false)


    useEffect(() => {
        const handleResize = () => {
          setIsMobile(window.innerWidth <= 640);
        };
    
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
      }, []);
    
    const itemsToShow = isMobile ? items.slice(0, 3) : items.slice(0, 5);
    


   return(
    <article className={` relative
        ${is_public == true && "flex flex-col min-h-45 w-full bg-neutral-950 rounded-lg text-white p-4 gap-2"}
        ${!showHidden && is_public == false && "hidden"} 
        ${showHidden && is_public == false && "flex flex-col min-h-45 w-full bg-neutral-950 rounded-lg border-dashed border-spacing-200 border-2 border-neutral-500 text-white p-4 gap-2"} 
        `}>
        <header className="flex justify-between ">
            <div className="flex flex-col items-start">
                <span className="flex flex-col-reverse">
                    <h3 className="font-semibold">{title}</h3>
                    <span>
                        {tag && matchListTag(tag)}
                    </span>
                    
                </span>
                <p className="text-xs text-neutral-400">{description}</p>
            </div>
            { showHidden && is_public == false &&
                <div className="absolute z-30 bg-black -top-4.25 right-[47.25%] p-1">
                    <EyeOff aria-label="hidden-item"/>
                </div>
            }
           
            
            <div>
                {
                    listTypeIcon(list_type)
                }
            </div>
        </header>
        <main className="flex flex-col gap-2">
            <div className="flex gap-2">
                {
                itemsToShow.map((item,index)=> (
                    <img key={index} className={`rounded-lg ${list_type == "shows" || list_type == "movies" ? "h-25 sm:h-30" : "h-22.5 sm:h-25"}`} src={item.image_url} />
                ))
                }
            </div>
            
            { profileUsername === username &&
                <div className="flex w-full gap-2">
                    <Button onClick={() => navigate(`/list/view/${id}`)} className="w-1/2 text-neutral-400 hover:cursor-pointer">View list</Button>
                    <Button onClick={() => navigate(`/list/edit/${id}`)} className="w-1/2 text-neutral-400 hover:cursor-pointer">Manage list</Button>
                </div>
            }

            { profileUsername !== username &&
                <Button onClick={() => navigate(`/list/view/${id}`)} className="w-full text-neutral-400 hover:cursor-pointer">View list</Button>
            }
          
        </main>
    </article>
   ) 
}

export interface EditProfileProps {
    bio:string;
    username:string;
    backgroundImage?:string;
    profilePicture?:string;
}


function EditProfileDialogue({username, bio, backgroundImage, profilePicture}:EditProfileProps) {
    const [activeTab, setActiveTab] = useState("customize");
    
    return(
        <Dialog>
        <DialogTrigger asChild>
            <div className="absolute top-15 left-20 md:left-15 z-20 hover:cursor-pointer flex items-center justify-center bg-black/50 backdrop-blur-xs text-white rounded-full p-2">
                <PencilLine className="w-5 h-5" />
            </div>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px] flex flex-col justify-center bg-foreground text-white pt-3">
            <Tabs defaultValue="edit-profile" className="flex w-full justify-center items-center">
                <TabsList className="bg-neutral-800 mb-3">
                    <TabsTrigger onClick={() => setActiveTab("edit")} value="edit-profile" 
                    className="dark">
                        Edit
                    </TabsTrigger>
                    <TabsTrigger onClick={() => setActiveTab("customize")} value="customize-profile" 
                    className="dark">
                        Customize
                    </TabsTrigger>
                </TabsList>
                <TabsContent className="flex flex-col gap-1" value="edit-profile">
                    <DialogHeader>
                        <DialogTitle>Edit profile</DialogTitle>
                        <DialogDescription>
                            Make changes to your profile here. Click save when you're done.
                        </DialogDescription>
                    </DialogHeader>
                        <EditProfileForm username={username} bio={bio} />
                    <DialogFooter>
                    </DialogFooter>
                </TabsContent>
                <TabsContent value="customize-profile">
                    <DialogHeader className="mb-2">
                        <DialogTitle>Customize profile</DialogTitle>
                        <DialogDescription>
                            Make your profile your own. Click save when you're done.
                        </DialogDescription>
                    </DialogHeader>

                    <CustomizeProfileForm />
                    <DialogFooter>

                    </DialogFooter>
                </TabsContent>
            </Tabs>
            
            
        </DialogContent>
    </Dialog>
    )
}


